import { useQuery } from "@tanstack/react-query";

import { VENUES, AJNA_POOL_INFO_UTILS } from "@/config/contracts";
import { AJNA_POOL_ABI } from "@/config/ajna-abi";
import { AJNA_POOL_INFO_UTILS_ABI } from "@/config/ajna-pool-abi";
import { buildPublicClient } from "@/lib/wallet/clients";

export type AjnaPoolData = {
  /** Pool inflator at 1e18 — scales t0debt → current debt. */
  readonly inflator: bigint;
  /** Total pool debt, 1e18 (USDC in WAD). */
  readonly totalDebt: bigint;
  /** Total quote-token (USDC) deposited, 1e18. */
  readonly depositSize: bigint;
  /** Available to borrow = depositSize - totalDebt, floored at 0. */
  readonly availableLiquidity: bigint;
  /** Pool base interest rate, 1e18. */
  readonly interestRate: bigint;
  /** Borrow APR as decimal (e.g. 0.085 = 8.5%). */
  readonly borrowApr: number;
  /** Lowest Utilised Price (quote per collateral), 1e18. */
  readonly lup: bigint;
  /** Bucket index of the LUP — use as default deposit bucket. */
  readonly lupIndex: bigint;
};

/**
 * Reads the Ajna CADC/USDC pool's accounting + pricing in a single
 * multicall batch. Borrow APR is reported as `interestRate / 1e18`
 * (Ajna treats this as a continuously-compounded annual base — close
 * enough to a displayed APR within fractions of a basis point).
 */
export const useAjnaPool = () =>
  useQuery({
    queryKey: ["ajna-pool", VENUES.ajna.contract],
    queryFn: async (): Promise<AjnaPoolData> => {
      const client = buildPublicClient();
      const pool = VENUES.ajna.contract;
      const [inflatorRes, debtRes, depositSizeRes, rateRes, pricesRes] =
        await client.multicall({
          allowFailure: true,
          contracts: [
            { address: pool, abi: AJNA_POOL_ABI, functionName: "inflatorInfo" },
            { address: pool, abi: AJNA_POOL_ABI, functionName: "debtInfo" },
            { address: pool, abi: AJNA_POOL_ABI, functionName: "depositSize" },
            { address: pool, abi: AJNA_POOL_ABI, functionName: "interestRateInfo" },
            {
              address: AJNA_POOL_INFO_UTILS,
              abi: AJNA_POOL_INFO_UTILS_ABI,
              functionName: "poolPricesInfo",
              args: [pool],
            },
          ],
        });
      if (
        inflatorRes.status !== "success" ||
        debtRes.status !== "success" ||
        depositSizeRes.status !== "success" ||
        rateRes.status !== "success"
      ) {
        throw new Error("Ajna pool core reads failed");
      }
      const inflator = inflatorRes.result[0];
      const totalDebt = debtRes.result[0];
      const depositSize = depositSizeRes.result;
      const interestRate = rateRes.result[0];
      // pricesInfo may not be available on all pool versions — fall back to 0.
      const lup = pricesRes.status === "success" ? pricesRes.result[4] : 0n;
      const lupIndex = pricesRes.status === "success" ? pricesRes.result[5] : 0n;
      const availableLiquidity =
        depositSize > totalDebt ? depositSize - totalDebt : 0n;
      const borrowApr = Number(interestRate) / 1e18;
      return {
        inflator,
        totalDebt,
        depositSize,
        availableLiquidity,
        interestRate,
        borrowApr,
        lup,
        lupIndex,
      };
    },
  });
